import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import SEO from "@/components/SEO";
import { SEO_KEYWORDS } from "@/seo/seoData";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Send,
  Mail,
  Phone,
  MapPin,
  Clock,
  MessageSquare,
  ArrowRight,
  Check,
} from "lucide-react";

const services = [
  "Website Development",
  "SEO Services",
  "Social Media Marketing",
  "Custom Tool Creation",
  "Maintenance & Support",
  "Not Sure Yet",
];

const contactInfo = [
  {
    icon: Mail,
    label: "Email",
    value: "INFO@SQUARETRIGGER.COM",
    href: "mailto:INFO@SQUARETRIGGER.COM",
  },
  {
    icon: Phone,
    label: "Phone",
    value: "236-869-335",
    href: "tel:236869335",
  },
  {
    icon: MapPin,
    label: "Location",
    value: "Vancouver, BC",
  },
  {
    icon: Clock,
    label: "Hours",
    value: "Mon – Fri: 9:00 AM – 6:00 PM PT",
  },
];

const initialFormData = {
  fullName: "",
  companyName: "",
  email: "",
  phone: "",
  service: "",
  projectDetails: "",
};

export default function ContactPage() {
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const emailRegex = useMemo(
    () => /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    []
  );

  const usPhoneRegex = useMemo(
    () =>
      /^(?:\+1\s?)?(?:\([2-9]\d{2}\)|[2-9]\d{2})[-.\s]?[0-9]{3}[-.\s]?[0-9]{4}$/,
    []
  );

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleServiceChange = (value) => {
    setFormData((prev) => ({ ...prev, service: value }));
    setErrors((prev) => ({
      ...prev,
      service: "",
    }));
  };

  const scrollToForm = () => {
    const formSection = document.getElementById("contact-form");
    if (formSection) {
      formSection.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = "Full name is required.";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email address is required.";
    } else if (!emailRegex.test(formData.email.trim())) {
      newErrors.email = "Please enter a valid email address.";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required.";
    } else if (!usPhoneRegex.test(formData.phone.trim())) {
      newErrors.phone = "Please enter a valid US phone number.";
    }

    if (!formData.service.trim()) {
      newErrors.service = "Please select a service.";
    }

    if (!formData.projectDetails.trim()) {
      newErrors.projectDetails = "Project details are required.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    await new Promise((resolve) => setTimeout(resolve, 1200));

    setIsSubmitting(false);
    setIsSubmitted(true);
    setFormData(initialFormData);
    setErrors({});
  };

  return (
    <Layout>
      <SEO
        title="Contact Us to know Us Better | Square Trigger"
        description="Get the best digital services. Contact Square Trigger now to transform your digital presence. Get in touch now."
        keywords={SEO_KEYWORDS.contact}
      />

      {/* Hero Section */}
      <section className="pt-24 sm:pt-28 lg:pt-32 pb-16 sm:pb-18 lg:pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-foreground via-foreground/95 to-foreground/90" />
        <div className="absolute inset-0">
          <div className="absolute top-16 sm:top-20 right-6 sm:right-20 w-72 sm:w-96 h-72 sm:h-72 bg-primary/20 rounded-full blur-3xl" />
          <div className="absolute bottom-16 sm:bottom-20 left-6 sm:left-20 w-56 sm:w-56 bg-secondary/20 rounded-full blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Badge className="mb-6 bg-primary/20 text-primary-foreground border-none">
                <MessageSquare className="h-4 w-4 mr-2" />
                Contact Us
              </Badge>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-background mb-5 sm:mb-6 leading-tight"
            >
              Let&apos;s Talk About What Your Business Needs
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-lg sm:text-xl text-background/70 mb-8 max-w-2xl mx-auto leading-relaxed"
            >
              Whether you need a better website, stronger visibility, or a
              custom digital solution, we&apos;re here to help.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex justify-center"
            >
              <Button
                variant="gradient"
                size="lg"
                onClick={scrollToForm}
                className="group w-full sm:w-auto justify-center"
              >
                <span className="flex items-center justify-center gap-2">
                  Send Us a Message
                  <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </span>
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Form + Details */}
      <section
        id="contact-form"
        className="pt-12 sm:pt-14 lg:pt-16 pb-8 sm:pb-10 lg:pb-12 bg-background relative overflow-hidden scroll-mt-24"
      >
        <div className="container-custom">
          {!isSubmitted ? (
            <div className="grid lg:grid-cols-5 gap-10 xl:gap-12 items-start">
              {/* Contact Form */}
              <motion.div
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="lg:col-span-3 order-2 lg:order-1 h-full"
              >
                <div className="bg-card rounded-2xl border border-border p-6 sm:p-8 shadow-soft h-full">
                  <div className="mb-6 sm:mb-8 text-center">
                    <span className="text-sm font-medium text-primary uppercase tracking-wider">
                      Contact Form
                    </span>
                    <h2 className="text-2xl sm:text-3xl font-display font-bold text-foreground mt-3 mb-3">
                      Tell Us About Your Project
                    </h2>
                    <p className="text-muted-foreground leading-relaxed max-w-2xl mx-auto">
                      Share a few details and we&apos;ll get back to you with the
                      right next step.
                    </p>
                  </div>

                  <form onSubmit={handleSubmit} noValidate>
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="fullName">Full Name</Label>
                        <Input
                          id="fullName"
                          name="fullName"
                          value={formData.fullName}
                          onChange={handleChange}
                          placeholder="Your full name"
                          required
                          className="h-12"
                        />
                        {errors.fullName && (
                          <p className="text-sm text-destructive">
                            {errors.fullName}
                          </p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="companyName">Company Name</Label>
                        <Input
                          id="companyName"
                          name="companyName"
                          value={formData.companyName}
                          onChange={handleChange}
                          placeholder="Your company name"
                          className="h-12"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="email">Email Address</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          inputMode="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="you@example.com"
                          required
                          className="h-12"
                        />
                        {errors.email && (
                          <p className="text-sm text-destructive">
                            {errors.email}
                          </p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone Number</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          inputMode="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="(555) 555-1234"
                          required
                          className="h-12"
                        />
                        {errors.phone && (
                          <p className="text-sm text-destructive">
                            {errors.phone}
                          </p>
                        )}
                      </div>

                      <div className="sm:col-span-2 space-y-2">
                        <Label htmlFor="service">Service Interested In</Label>
                        <Select
                          value={formData.service}
                          onValueChange={handleServiceChange}
                        >
                          <SelectTrigger className="h-12">
                            <SelectValue placeholder="Select a service" />
                          </SelectTrigger>
                          <SelectContent>
                            {services.map((service) => (
                              <SelectItem key={service} value={service}>
                                {service}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {errors.service && (
                          <p className="text-sm text-destructive">
                            {errors.service}
                          </p>
                        )}
                      </div>

                      <div className="sm:col-span-2 space-y-2">
                        <Label htmlFor="projectDetails">Project Details</Label>
                        <Textarea
                          id="projectDetails"
                          name="projectDetails"
                          value={formData.projectDetails}
                          onChange={handleChange}
                          placeholder="Tell us what you need, what is not working right now, or what you want to improve."
                          rows={6}
                          required
                          className="resize-none min-h-[150px]"
                        />
                        {errors.projectDetails && (
                          <p className="text-sm text-destructive">
                            {errors.projectDetails}
                          </p>
                        )}
                      </div>
                    </div>

                    <Button
                      type="submit"
                      variant="gradient"
                      size="lg"
                      className="w-full mt-8 group"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>Sending...</>
                      ) : (
                        <>
                          Send Message
                          <Send className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                        </>
                      )}
                    </Button>
                  </form>
                </div>
              </motion.div>

              {/* Contact Details */}
              <motion.div
                initial={{ opacity: 0, x: -40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="lg:col-span-2 order-1 lg:order-2 h-full"
              >
                <div className="h-full flex flex-col">
                  <div className="text-center mb-8">
                    <span className="text-sm font-medium text-primary uppercase tracking-wider block">
                      Contact Details
                    </span>
                    <h2 className="text-2xl sm:text-3xl font-display font-bold text-foreground mt-3">
                      Get in Touch
                    </h2>
                  </div>

                  <div className="space-y-6">
                    {contactInfo.map((item, index) => {
                      const Icon = item.icon;
                      const content = (
                        <div className="flex items-start gap-4 min-h-[72px]">
                          <div className="p-3 rounded-xl bg-accent shrink-0">
                            <Icon className="h-5 w-5 text-primary" />
                          </div>
                          <div className="min-w-0 pt-0.5">
                            <p className="text-sm text-muted-foreground">
                              {item.label}
                            </p>
                            <p className="font-medium text-foreground break-words">
                              {item.value}
                            </p>
                          </div>
                        </div>
                      );

                      return (
                        <motion.div
                          key={item.label}
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.5, delay: index * 0.08 }}
                          className="bg-card rounded-2xl border border-border p-5 shadow-soft"
                        >
                          {item.href ? (
                            <a
                              href={item.href}
                              className="block hover:opacity-80 transition-opacity"
                            >
                              {content}
                            </a>
                          ) : (
                            content
                          )}
                        </motion.div>
                      );
                    })}
                  </div>

                  <div className="bg-card rounded-2xl border border-border p-5 shadow-soft mt-6">
                    <div className="min-h-[72px] flex flex-col justify-center">
                      <p className="text-sm text-muted-foreground mb-1">Note</p>
                      <p className="font-medium text-foreground">
                        Remote-first agency. Meetings by appointment only.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-card rounded-[2rem] border border-border shadow-soft min-h-[520px] flex items-center justify-center px-6 py-12"
            >
              <div className="text-center max-w-2xl mx-auto">
                <div className="w-20 h-20 rounded-full bg-emerald-500 flex items-center justify-center mx-auto mb-8 shadow-lg">
                  <Check className="h-10 w-10 text-white" />
                </div>

                <h2 className="text-3xl sm:text-4xl font-display font-bold text-foreground mb-4">
                  Thank you!
                </h2>

                <p className="text-base sm:text-lg text-muted-foreground mb-10 leading-relaxed">
                  Thanks for reaching out. We’ve received your message and will
                  get back to you soon.
                </p>

                <Button
                  variant="gradient"
                  size="lg"
                  asChild
                  className="group px-8"
                >
                  <Link
                    to="/"
                    className="flex items-center justify-center gap-2"
                  >
                    Back to Home
                    <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
              </div>
            </motion.div>
          )}
        </div>
      </section>
    </Layout>
  );
}